/**
 * Posições de jogador por esporte (código estável em `GameParticipant.preferredPosition`).
 * Vôlei de quadra e areia usam listas diferentes via `sportCategory`.
 */
export const VOLEI_INDOOR_POSITIONS = [
  { value: "LEVANTADOR", label: "Levantador" },
  { value: "OPOSTO", label: "Oposto" },
  { value: "CENTRAL", label: "Central" },
  { value: "LIBERO", label: "Líbero" },
  { value: "PONTA", label: "Ponta" },
] as const;

export const VOLEI_SAND_POSITIONS = [
  { value: "LEVANTADOR", label: "Levantador" },
  { value: "OPOSTO", label: "Oposto" },
  { value: "CENTRAL", label: "Central" },
  { value: "PONTEIRO", label: "Ponteiro" },
] as const;

export const SPORT_POSITION_OPTIONS: Record<
  string,
  readonly { value: string; label: string }[]
> = {
  FUTEBOL: [
    { value: "GOLEIRO", label: "Goleiro" },
    { value: "ZAGUEIRO", label: "Zagueiro" },
    { value: "LATERAL", label: "Lateral" },
    { value: "VOLANTE", label: "Volante" },
    { value: "MEIA", label: "Meia" },
    { value: "ATACANTE", label: "Atacante" },
  ],
  VOLEI: [...VOLEI_INDOOR_POSITIONS],
  BEACH_TENNIS: [
    { value: "DIREITA", label: "Direita" },
    { value: "ESQUERDA", label: "Esquerda" },
  ],
  CARIMBA: [
    { value: "ATAQUE", label: "Ataque" },
    { value: "DEFESA", label: "Defesa" },
  ],
  FUTEVOLEI: [
    { value: "FRENTE_ESQ", label: "Frente esq." },
    { value: "FRENTE_DIR", label: "Frente dir." },
    { value: "FUNDO_ESQ", label: "Fundo esq." },
    { value: "FUNDO_DIR", label: "Fundo dir." },
  ],
};

/** Posições por categoria dentro do esporte (ex.: vôlei quadra vs areia). */
export const SPORT_CATEGORY_POSITION_OPTIONS: Record<
  string,
  Record<string, readonly { value: string; label: string }[]>
> = {
  VOLEI: {
    VOL_INDOOR: VOLEI_INDOOR_POSITIONS,
    VOL_SAND: VOLEI_SAND_POSITIONS,
  },
};

export function getSportPositionsForSport(
  sport: string | undefined | null,
  sportCategory?: string | null,
): { value: string; label: string }[] {
  if (!sport) return [];
  const category = sportCategory?.trim();
  if (category && SPORT_CATEGORY_POSITION_OPTIONS[sport]?.[category]) {
    return [...SPORT_CATEGORY_POSITION_OPTIONS[sport][category]];
  }
  if (!SPORT_POSITION_OPTIONS[sport]) return [];
  return [...SPORT_POSITION_OPTIONS[sport]];
}

export function getSportPositionLabel(
  sport: string | undefined | null,
  position: string | null | undefined,
  sportCategory?: string | null,
): string | undefined {
  if (!position?.trim()) return undefined;
  const list = getSportPositionsForSport(sport, sportCategory);
  const found = list.find((p) => p.value === position);
  if (found) return found.label;
  if (sport === "VOLEI") {
    const legacySand = [
      { value: "ATACANTE", label: "Atacante" },
      { value: "FUNDO", label: "Fundo" },
      { value: "DEFESA", label: "Defesa" },
      { value: "ATAQUE", label: "Ataque" },
    ];
    const allVolley = [...VOLEI_INDOOR_POSITIONS, ...VOLEI_SAND_POSITIONS, ...legacySand];
    return allVolley.find((p) => p.value === position)?.label ?? position;
  }
  const fallback = SPORT_POSITION_OPTIONS[sport ?? ""];
  if (!fallback) return position;
  return fallback.find((p) => p.value === position)?.label ?? position;
}

export function sportHasPositions(
  sport: string | undefined | null,
  sportCategory?: string | null,
): boolean {
  return getSportPositionsForSport(sport, sportCategory).length > 0;
}
